import { useInfiniteQuery } from '@tanstack/react-query';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import clientApi, { mapCreatorsFromApiModelToViewModel } from '~/clientApi';
import { sortDirections, sortFields } from '~/clientApi/agencies.model';
import { AGENCIES_COLLECTION_QUERY_KEY } from '~/queries/keys';
import { useSearchTalentStore } from '~/screens/MyAgency/store';

import { useListAgencies } from './useListAgencies';

const PAGE_SIZE = 20;

export type useListAgencyCreatorProps = {
  sortField: sortFields;
  sortDirection: sortDirections;
  filters: string[];
  slug: string;
};

export const useListAgencyCreators = ({
  sortField,
  sortDirection,
  filters,
  slug,
}: useListAgencyCreatorProps) => {
  // This hook useAgencyListCreators was written when we were using RQ v2
  // Even though this still works, since our stack upgraded to RQ v4, we can make this hook cleaner and easier to read
  // If you are working on this hook, consider rewriting the logic of this hook with the new RQ specs/functionalities.

  const { query } = useSearchTalentStore();

  const agency = useListAgencies(slug);
  // typescript v5 update issue
  const agencyId = (agency?.data as any)?.results[0].id;
  const infiniteQueryKey = [
    AGENCIES_COLLECTION_QUERY_KEY,
    { agencyId, sort: sortField, direction: sortDirection, filters, query },
  ];

  const {
    data = {
      pages: [],
    },
    ...rest
  } = useInfiniteQuery(
    infiniteQueryKey,
    ({ pageParam: page }) => {
      if (typeof page !== 'number') {
        if (page === false) {
          return;
        }
        // For some reason the initial value of `page` is a uuid
        page = 1;
      }

      return clientApi.getAgencyCreators({
        agencyId,
        slug,
        filters: [...filters, `keyword:${query}`],
        sort: `${sortField}:${sortDirection}`,
        page,
        pageSize: PAGE_SIZE,
      });
    },
    {
      enabled: Boolean(agencyId),
      getNextPageParam: (page, pages) =>
        Math.floor(pages.length * PAGE_SIZE) <= (page?.totalItems ?? 0)
          ? pages.length + 1
          : undefined,
      staleTime: 1000 * 60 * 5, // 5 minutes stale time
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    },
  );

  const creatorsResult =
    data.pages
      .filter((page: any) => !isEmpty(page?.results))
      .map((page: any) => {
        return page.results;
      })
      ?.flat() ?? [];
  const creators = mapCreatorsFromApiModelToViewModel(creatorsResult);

  const [isInitialFetch, setIsInitialFetch] = useState(true);

  // update isInitialFetch when data is loaded
  useEffect(() => {
    if (data && data.pages.filter((page) => !isEmpty(page)).length > 0) {
      setIsInitialFetch(false);
    }
  }, [data]);

  const totalCreators = data.pages[0]?.totalItems ?? 0;
  const hasFinishedLoadingCreators = totalCreators === creators.length;

  return {
    data: creators,
    totalCreators,
    infiniteQueryKey,
    isInitialFetch,
    hasFinishedLoadingCreators,
    ...rest,
  };
};
